import { supabase } from './supabase';
import { RealtimeChannel } from '@supabase/supabase-js';

type RealtimeCallback = (payload: any) => void;

class RealtimeManager {
  private channels: Map<string, RealtimeChannel> = new Map();
  private callbacks: Map<string, Set<RealtimeCallback>> = new Map();
  private cleanupTimeouts: Map<string, number> = new Map();

  // Subscribe to property changes for a specific customer
  subscribeToProperties(kundeId: string, callback: RealtimeCallback) {
    const channelName = `properties-${kundeId}`;
    
    if (!this.channels.has(channelName)) {
      const channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'immobilien',
            filter: `kunde_id=eq.${kundeId}`
          },
          callback
        )
        .subscribe();
      
      this.channels.set(channelName, channel);
      this.callbacks.set(channelName, new Set([callback]));
    } else {
      // Add callback to existing channel
      const callbacks = this.callbacks.get(channelName);
      if (callbacks) {
        callbacks.add(callback);
      }
    }

    // Return unsubscribe function
    return () => {
      const callbacks = this.callbacks.get(channelName);
      if (callbacks) {
        callbacks.delete(callback);
        
        // If no more callbacks, remove the channel
        if (callbacks.size === 0) {
          // Use requestAnimationFrame instead of setTimeout for cleanup
          const timeoutId = requestAnimationFrame(() => {
            const channel = this.channels.get(channelName);
            if (channel) {
              supabase.removeChannel(channel);
              this.channels.delete(channelName);
              this.callbacks.delete(channelName);
            }
            this.cleanupTimeouts.delete(channelName);
          });
          this.cleanupTimeouts.set(channelName, timeoutId);
        }
      }
    };
  }

  // Subscribe to notes changes for a specific property
  subscribeToNotes(immobilienId: string, callback: RealtimeCallback) {
    const channelName = `notes-${immobilienId}`;
    
    if (!this.channels.has(channelName)) {
      const channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'notizen',
            filter: `immobilien_id=eq.${immobilienId}`
          },
          callback
        )
        .subscribe();
      
      this.channels.set(channelName, channel);
      this.callbacks.set(channelName, new Set([callback]));
    } else {
      const callbacks = this.callbacks.get(channelName);
      if (callbacks) {
        callbacks.add(callback);
      }
    }

    return () => {
      const callbacks = this.callbacks.get(channelName);
      if (callbacks) {
        callbacks.delete(callback);
        
        if (callbacks.size === 0) {
          // Use requestAnimationFrame instead of setTimeout for cleanup
          const timeoutId = requestAnimationFrame(() => {
            const channel = this.channels.get(channelName);
            if (channel) {
              supabase.removeChannel(channel);
              this.channels.delete(channelName);
              this.callbacks.delete(channelName);
            }
            this.cleanupTimeouts.delete(channelName);
          });
          this.cleanupTimeouts.set(channelName, timeoutId);
        }
      }
    };
  }

  // Subscribe to customer profile changes
  subscribeToProfile(kundeId: string, callback: RealtimeCallback) {
    const channelName = `profile-${kundeId}`;
    
    if (!this.channels.has(channelName)) {
      const channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'kunden',
            filter: `id=eq.${kundeId}`
          },
          callback
        )
        .subscribe();
      
      this.channels.set(channelName, channel);
      this.callbacks.set(channelName, new Set([callback]));
    } else {
      const callbacks = this.callbacks.get(channelName);
      if (callbacks) {
        callbacks.add(callback);
      }
    }

    return () => {
      const callbacks = this.callbacks.get(channelName);
      if (callbacks) {
        callbacks.delete(callback);
        
        if (callbacks.size === 0) {
          // Use requestAnimationFrame instead of setTimeout for cleanup
          const timeoutId = requestAnimationFrame(() => {
            const channel = this.channels.get(channelName);
            if (channel) {
              supabase.removeChannel(channel);
              this.channels.delete(channelName);
              this.callbacks.delete(channelName);
            }
            this.cleanupTimeouts.delete(channelName);
          });
          this.cleanupTimeouts.set(channelName, timeoutId);
        }
      }
    };
  }

  // Clean up all subscriptions
  cleanup() {
    // Cancel any pending cleanup timeouts
    this.cleanupTimeouts.forEach((timeoutId) => {
      cancelAnimationFrame(timeoutId);
    });
    this.cleanupTimeouts.clear();
    
    this.channels.forEach((channel) => {
      supabase.removeChannel(channel);
    });
    this.channels.clear();
    this.callbacks.clear();
  }
}

export const realtimeManager = new RealtimeManager();